import { Calendar, Users, UserPlus, DollarSign, ThumbsUp, ThumbsDown } from 'lucide-react';

const USD_TO_INR = 83.5;

interface KnowledgeAssistantCardProps {
  name: string;
  status: 'active' | 'inactive';
  totalUsers: number;
  newUsersToday: number;
  costToday: number;
  totalCost: number;
  thumbsUpPercentage?: number;
  thumbsDownPercentage?: number;
  loading?: boolean;
}

function formatDualCurrency(usd: number) {
  return {
    inr: `₹${(usd * USD_TO_INR).toFixed(2)}`,
    usd: `$${usd.toFixed(2)}`,
  };
}

export function KnowledgeAssistantCard({
  name,
  status,
  totalUsers,
  newUsersToday,
  costToday,
  totalCost,
  thumbsUpPercentage,
  thumbsDownPercentage,
  loading = false,
}: KnowledgeAssistantCardProps) {
  if (loading) {
    return (
      <div className="bg-white border border-gray-200 rounded-lg p-5">
        <div className="animate-pulse space-y-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-gray-200" />
            <div className="space-y-1.5 flex-1">
              <div className="h-3 bg-gray-200 rounded w-2/3" />
              <div className="h-3 bg-gray-200 rounded w-1/3" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3 mt-4">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="h-12 bg-gray-200 rounded" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  const costTodayFmt = formatDualCurrency(costToday);
  const totalCostFmt = formatDualCurrency(totalCost);

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-5 hover:shadow-md transition-shadow">
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center">
            <Calendar className="w-5 h-5 text-blue-600" />
          </div>
          <div>
            <h3 className="font-semibold text-foreground">{name}</h3>
            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
              status === 'active'
                ? 'bg-green-50 text-green-700 border border-green-200'
                : 'bg-gray-50 text-gray-700 border border-gray-200'
            }`}>
              {status}
            </span>
          </div>
        </div>
      </div>

      {/* Metrics Grid */}
      <div className="grid grid-cols-2 gap-4">
        {/* Total Users */}
        <div className="flex items-start gap-2">
          <div className="w-8 h-8 rounded bg-green-50 flex items-center justify-center flex-shrink-0">
            <Users className="w-4 h-4 text-green-600" />
          </div>
          <div className="min-w-0">
            <p className="text-xs text-muted-foreground">Total Users</p>
            <p className="text-lg font-semibold text-foreground">{totalUsers}</p>
          </div>
        </div>

        {/* New Users Today */}
        <div className="flex items-start gap-2">
          <div className="w-8 h-8 rounded bg-purple-50 flex items-center justify-center flex-shrink-0">
            <UserPlus className="w-4 h-4 text-purple-600" />
          </div>
          <div className="min-w-0">
            <p className="text-xs text-muted-foreground">New Today</p>
            <p className="text-lg font-semibold text-foreground">{newUsersToday}</p>
          </div>
        </div>

        {/* Cost Today */}
        <div className="flex items-start gap-2">
          <div className="w-8 h-8 rounded bg-orange-50 flex items-center justify-center flex-shrink-0">
            <DollarSign className="w-4 h-4 text-orange-600" />
          </div>
          <div className="min-w-0">
            <p className="text-xs text-muted-foreground">Cost Today</p>
            <p className="text-base font-semibold text-foreground">{costTodayFmt.inr}</p>
            <p className="text-xs text-muted-foreground">{costTodayFmt.usd}</p>
          </div>
        </div>

        {/* Total Cost */}
        <div className="flex items-start gap-2">
          <div className="w-8 h-8 rounded bg-blue-50 flex items-center justify-center flex-shrink-0">
            <DollarSign className="w-4 h-4 text-blue-600" />
          </div>
          <div className="min-w-0">
            <p className="text-xs text-muted-foreground">Total Cost</p>
            <p className="text-base font-semibold text-foreground">{totalCostFmt.inr}</p>
            <p className="text-xs text-muted-foreground">{totalCostFmt.usd}</p>
          </div>
        </div>

        {/* User Feedback */}
        {thumbsUpPercentage !== undefined && thumbsDownPercentage !== undefined && (
          <div className="flex items-start gap-2 col-span-2">
            <div className="min-w-0">
              <p className="text-xs text-muted-foreground mb-1">User Feedback</p>
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-1.5">
                  <ThumbsUp className="w-4 h-4 text-green-600" />
                  <span className="text-base font-semibold text-foreground">{thumbsUpPercentage}%</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <ThumbsDown className="w-4 h-4 text-red-600" />
                  <span className="text-base font-semibold text-foreground">{thumbsDownPercentage}%</span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
